import { Text } from "@medusajs/ui"
import { Section } from "@/components/ui"

/**
 * Shopify-style order risk from what the store actually knows: the card network's
 * address (AVS) and security-code (CVV) checks returned by Authorize.net, its
 * fraud filter hold, and order details that often go with fraud. A guide for
 * staff before shipping, not a verdict.
 */
type Level = "low" | "medium" | "high"
type Signal = { level: Level; text: string }

const DISPOSABLE = new Set(["mailinator.com", "guerrillamail.com", "10minutemail.com", "tempmail.com", "temp-mail.org", "yopmail.com", "trashmail.com", "sharklasers.com", "getnada.com", "dispostable.com", "maildrop.cc", "throwawaymail.com"])

export function riskSignals(o: any): { level: Level | null; signals: Signal[] } {
  const signals: Signal[] = []
  const payments: any[] = (o.payment_collections ?? []).flatMap((pc: any) => pc.payments ?? [])
  const card = payments.find((p) => p.provider_id === "pp_authorizenet_authorizenet" && p.data?.transaction_id)?.data
  if (card) {
    if (card.held_for_review) signals.push({ level: "high", text: "Authorize.net's fraud filters held this payment for review." })
    if (card.cvv === "N") signals.push({ level: "high", text: "The card security code (CVV) didn't match." })
    if (card.avs === "N") signals.push({ level: "high", text: "Neither the billing street nor the ZIP code matched the card." })
    else if (["A", "W", "Z"].includes(card.avs)) signals.push({ level: "medium", text: card.avs === "A" ? "Billing street matched, but the ZIP code didn't." : "Billing ZIP code matched, but the street didn't." })
    if (card.avs === "Y" || card.avs === "X") signals.push({ level: "low", text: "Billing address matched the card." })
    if (card.cvv === "M") signals.push({ level: "low", text: "Card security code (CVV) matched." })
  }
  const ship = o.shipping_address
  const bill = o.billing_address
  if (ship?.country_code && bill?.country_code && ship.country_code.toLowerCase() !== bill.country_code.toLowerCase())
    signals.push({ level: "medium", text: `Billing country (${bill.country_code.toUpperCase()}) differs from the shipping country (${ship.country_code.toUpperCase()}).` })
  const name = (a: any) => [a?.first_name, a?.last_name].filter(Boolean).join(" ").trim().toLowerCase()
  if (ship && bill && name(ship) && name(bill) && name(ship) !== name(bill)) signals.push({ level: "low", text: "Billing and shipping names are different (often a gift)." })
  const domain = String(o.email ?? "").split("@")[1]?.toLowerCase()
  if (domain && DISPOSABLE.has(domain)) signals.push({ level: "medium", text: `The email uses a throwaway domain (${domain}).` })
  const bigQty = (o.items ?? []).some((i: any) => Number(i.quantity) >= 10)
  if (bigQty) signals.push({ level: "low", text: "Large quantity of one item." })
  if (!card) return { level: signals.some((s) => s.level !== "low") ? (signals.some((s) => s.level === "high") ? "high" : "medium") : null, signals }
  const level: Level = signals.some((s) => s.level === "high") ? "high" : signals.some((s) => s.level === "medium") ? "medium" : "low"
  return { level, signals }
}

const COPY: Record<Level, { title: string; body: string; color: string; width: string }> = {
  low: { title: "This order is low risk", body: "The checks we have look normal. You can fulfill this order.", color: "#29845a", width: "33%" },
  medium: { title: "This order has some risk", body: "Review the details below before you ship. Contact the customer if something doesn't add up.", color: "#b28400", width: "66%" },
  high: { title: "This order is high risk", body: "Several checks failed. Consider contacting the customer, or cancel and refund before shipping.", color: "#c5280c", width: "100%" },
}

export function OrderRiskCard({ order }: { order: any }) {
  const { level, signals } = riskSignals(order)
  return (
    <Section title="Order risk">
      <div className="flex flex-col gap-2 px-4 py-3">
        {level ? (
          <>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--k-fill-strong)]" aria-hidden="true">
              <div className="h-full rounded-full" style={{ width: COPY[level].width, background: COPY[level].color }} />
            </div>
            <div className="flex justify-between text-[11px] text-ui-fg-muted" aria-hidden="true"><span>Low</span><span>Medium</span><span>High</span></div>
            <Text size="small" weight="plus" style={{ color: COPY[level].color }}>{COPY[level].title}</Text>
            <Text size="xsmall" className="text-ui-fg-subtle">{COPY[level].body}</Text>
          </>
        ) : (
          <Text size="small" className="text-ui-fg-subtle">Not enough information to assess risk (no card checks on this order).</Text>
        )}
        {signals.length > 0 && (
          <ul className="mt-1 flex flex-col gap-1 text-[12px]">
            {signals.map((s) => (
              <li key={s.text} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: s.level === "high" ? "#c5280c" : s.level === "medium" ? "#b28400" : "#29845a" }} aria-hidden="true" />
                <span>{s.text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  )
}
